import { boundary, cameraPosts, gateAt, structures } from '../lib/site-plan';

/*
 * The still is drawn on a PORTRAIT sheet.
 *
 * It fills its box with `slice`, so whichever axis is relatively shorter gets
 * cropped. The old 960x600 sheet matched a 1440x900 desktop exactly and cropped
 * 71% of the width on a 390x844 phone — a reader with motion turned down saw a
 * corner of one structure and some grid, with no boundary, no camera posts and
 * no gate. Drawing on 600x900 inverts that: a phone crops 14% of each side and
 * still reads as a surveyed stand, and a desktop crops the empty top and bottom
 * instead of the plan.
 *
 * SAFE BOX — everything below lives inside x 84..517 and y 286..614, which is
 * what survives `slice` from 360px wide to 1440px wide.
 */
const SCALE = 47;
const ORIGIN_X = 305;
const ORIGIN_Y = 450;
const SHEET_W = 600;
const SHEET_H = 900;

function toX(value: number): number {
  return ORIGIN_X + value * SCALE;
}

function toY(value: number): number {
  return ORIGIN_Y + value * SCALE;
}

const boundaryPath = boundary
  .map(([x, z]) => `${toX(x).toFixed(1)},${toY(z).toFixed(1)}`)
  .join(' ');

/**
 * The still the canvas falls back to.
 *
 * Same plan, same data, drawn flat as a survey sheet — so a reader with WebGL
 * off, or motion turned down, gets an art-directed frame rather than a hole.
 */
export function PerimeterPlanStill({ title }: Props) {
  return (
    <svg
      viewBox={`0 0 ${SHEET_W} ${SHEET_H}`}
      role="img"
      aria-label={title}
      className="h-full w-full"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id="plan-grid" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M24 0H0V24" fill="none" stroke="#232a36" strokeWidth="1" />
        </pattern>
        <radialGradient id="plan-sweep" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#f04e23" stopOpacity="0.26" />
          <stop offset="100%" stopColor="#f04e23" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width={SHEET_W} height={SHEET_H} fill="#0b0e13" />
      <rect width={SHEET_W} height={SHEET_H} fill="url(#plan-grid)" />
      <circle cx={ORIGIN_X} cy={ORIGIN_Y} r="260" fill="url(#plan-sweep)" />

      <polygon points={boundaryPath} fill="none" stroke="#eae7dd" strokeOpacity="0.66" strokeWidth="2" />

      {structures.map(([cx, cz, hw, hd]) => (
        <rect
          key={`${cx}:${cz}`}
          x={toX(cx - hw)}
          y={toY(cz - hd)}
          width={hw * 2 * SCALE}
          height={hd * 2 * SCALE}
          fill="none"
          stroke="#eae7dd"
          strokeOpacity="0.4"
          strokeWidth="1.5"
        />
      ))}

      <line
        x1={ORIGIN_X}
        y1={ORIGIN_Y}
        x2={toX(4.9)}
        y2={toY(-1.4)}
        stroke="#f04e23"
        strokeWidth="1.5"
        strokeOpacity="0.85"
      />

      {cameraPosts.map(([x, z]) => (
        <g key={`post-${x}:${z}`}>
          <rect x={toX(x) - 4} y={toY(z) - 4} width="8" height="8" fill="#f04e23" />
          <circle cx={toX(x)} cy={toY(z)} r="20" fill="none" stroke="#f04e23" strokeOpacity="0.32" strokeWidth="1" />
        </g>
      ))}

      <rect x={toX(gateAt[0]) - 24} y={toY(gateAt[1]) - 2.5} width="48" height="5" fill="#f04e23" fillOpacity="0.7" />
    </svg>
  );
}

interface Props {
  title: string;
}
